import React from 'react';

import { BOARD_COLOR_PALETTE } from '@aline/common';
import { Popover, Typography } from '@mui/material';
import { CheckMarkIcon } from 'src/components/icons/CheckMarkIcon';
import { IconColors } from 'src/components/icons/icon-utils';
import tinycolor from 'tinycolor2';
import { makeStyles } from 'tss-react/mui';

export { COLUMN_CATEGORY_COLORS, DEFAULT_COLUMN_COLOR, getDefaultColumnColor } from '@aline/common';

const useColorGridStyles = makeStyles()(theme => ({
  colorCircle: {
    alignItems: 'center',
    border: '2px solid transparent',
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    height: 20,
    justifyContent: 'center',
    transition: 'transform 0.2s',
    width: 20,
  },
  colorCircleSelected: {
    transform: 'scale(1.1)',
  },
  colorGrid: {
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns: 'repeat(8, 1fr)',
  },
}));

const usePopoverStyles = makeStyles()(theme => ({
  container: {
    maxWidth: 400,
    padding: theme.spacing(2),
  },
  title: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
}));

interface ColorGridProps {
  onColorSelect: (color: string) => void;
  selectedColor?: string | null;
}

export function ColorGrid({ onColorSelect, selectedColor }: ColorGridProps): JSX.Element {
  const { classes } = useColorGridStyles();

  return (
    <div className={classes.colorGrid}>
      {BOARD_COLOR_PALETTE.map(color => {
        const tinyColor = tinycolor(color);
        const isSelected = color === selectedColor;

        return (
          <div
            key={color}
            aria-label={`Select color ${color}`}
            className={`${classes.colorCircle} ${isSelected ? classes.colorCircleSelected : ''}`}
            role="button"
            style={{ backgroundColor: color }}
            tabIndex={0}
            onClick={() => onColorSelect(color)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onColorSelect(color);
              }
            }}
          >
            {isSelected && (
              <CheckMarkIcon fill={tinyColor.isDark() ? IconColors.White : IconColors.DarkGrey} />
            )}
          </div>
        );
      })}
    </div>
  );
}

interface ColorPickerPopoverProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onColorSelect: (color: string) => void;
  open: boolean;
  selectedColor?: string | null;
}

export function ColorPickerPopover({
  anchorEl,
  onClose,
  onColorSelect,
  open,
  selectedColor,
}: ColorPickerPopoverProps): JSX.Element {
  const { classes } = usePopoverStyles();

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{
        horizontal: 'left',
        vertical: 'bottom',
      }}
      open={open}
      transformOrigin={{
        horizontal: 'left',
        vertical: 'top',
      }}
      onClose={onClose}
    >
      <div className={classes.container}>
        <Typography className={classes.title} variant="subtitle2">
          Color
        </Typography>
        <ColorGrid
          selectedColor={selectedColor}
          onColorSelect={color => {
            onColorSelect(color);
            onClose();
          }}
        />
      </div>
    </Popover>
  );
}
